import { Injectable, inject } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";

import { BehaviorSubject } from "rxjs";

export interface PendingNotification {
  message: string;
  duration?: number;
  action?: string;
  type?: "success" | "error" | "info";
}

const DEFAULT_DURATION = 3000;
const ERROR_DURATION = 6000;

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly pendingNotification =
    new BehaviorSubject<PendingNotification | null>(null);

  setPendingNotification(notification: PendingNotification): void {
    this.pendingNotification.next(notification);
  }

  showPendingNotification(): void {
    const notification = this.pendingNotification.value;
    if (notification) {
      const panelClass = this.getPanelClass(notification.type);

      this.snackBar.open(notification.message, notification.action, {
        duration: notification.duration ?? this.defaultDuration(notification.type),
        horizontalPosition: "right",
        verticalPosition: "top",
        panelClass: panelClass,
      });

      this.pendingNotification.next(null);
    }
  }

  show(notification: PendingNotification): void {
    const panelClass = this.getPanelClass(notification.type);

    this.snackBar.open(notification.message, notification.action, {
      duration: notification.duration ?? this.defaultDuration(notification.type),
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: panelClass,
    });
  }

  private defaultDuration(type?: "success" | "error" | "info"): number {
    return type === "error" ? ERROR_DURATION : DEFAULT_DURATION;
  }

  success(message: string, duration?: number): void {
    this.show({ message, type: "success", duration });
  }

  error(message: string, duration?: number): void {
    this.show({ message, type: "error", duration });
  }

  info(message: string, duration?: number): void {
    this.show({ message, type: "info", duration });
  }

  private getPanelClass(type?: "success" | "error" | "info"): string[] {
    switch (type) {
      case "success":
        return ["snackbar-success"];
      case "error":
        return ["snackbar-error"];
      case "info":
        return ["snackbar-info"];
      default:
        return [];
    }
  }
}
